/**
 * Design tokens. This is the SINGLE source of truth for the visual language.
 * Re-skinning the app = editing this file (and `theme.css` variable defaults).
 *
 * Tokens are emitted as CSS custom properties at runtime (see `applyTheme`),
 * so feature components reference `var(--color-...)` and never hardcode colors.
 */

export interface ThemeTokens {
  color: Record<string, string>;
  space: Record<string, string>;
  radius: Record<string, string>;
  font: Record<string, string>;
  shadow: Record<string, string>;
}

/** Claim Matrix brand theme: deep navy chrome, muted-green accents, Inter. */
export const defaultTheme: ThemeTokens = {
  color: {
    accent: "#11823b",
    accentDark: "#0d6e31",
    accentContrast: "#ffffff",
    bg: "#ffffff",
    bgSubtle: "#f5f8fb",
    bgChat: "#eef2f7",
    panel: "#ffffff",
    border: "#dbe2e9",
    text: "#081e39",
    textMuted: "#596571",
    textInverse: "#f7fbfd",
    headerBg: "#304060",
    headerText: "#f7fbfd",
    headerMuted: "rgba(247, 251, 253, 0.72)",
    bubbleOut: "#304060",
    bubbleIn: "#ffffff",
    bubbleOutText: "#f7fbfd",
    bubbleInText: "#081e39",
    unread: "#11823b",
    online: "#11823b",
    danger: "#db2b33",
    receiptRead: "#53bdeb",
    receiptSent: "#8aa0b1",
    overlay: "rgba(8, 30, 57, 0.45)",
  },
  space: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    xxl: "32px",
  },
  radius: {
    sm: "8px",
    md: "12px",
    lg: "16px",
    pill: "999px",
  },
  font: {
    family:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    sizeSm: "12px",
    sizeMd: "14px",
    sizeLg: "16px",
    sizeXl: "20px",
    weightNormal: "400",
    weightMedium: "500",
    weightBold: "600",
  },
  shadow: {
    sm: "0 1px 2px rgba(11,20,26,0.08)",
    md: "0 2px 8px rgba(11,20,26,0.12)",
  },
};

/**
 * Convert a token group into CSS custom property declarations.
 * e.g. color.accent -> --color-accent
 */
function toCssVars(prefix: string, group: Record<string, string>): [string, string][] {
  return Object.entries(group).map(([key, value]) => {
    const kebab = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
    return [`--${prefix}-${kebab}`, value];
  });
}

/** Flatten all token groups into CSS variable pairs. */
export function tokensToCssVars(theme: ThemeTokens): [string, string][] {
  return [
    ...toCssVars("color", theme.color),
    ...toCssVars("space", theme.space),
    ...toCssVars("radius", theme.radius),
    ...toCssVars("font", theme.font),
    ...toCssVars("shadow", theme.shadow),
  ];
}

/** Apply the theme by setting CSS custom properties on the given element. */
export function applyTheme(theme: ThemeTokens, target: HTMLElement): void {
  for (const [name, value] of tokensToCssVars(theme)) {
    target.style.setProperty(name, value);
  }
}
